import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { getApiEndpoint } from '../config/api'
import SessionTimeoutModal from '../components/SessionTimeoutModal'

const AuthContext = createContext(null)

// Token expiry constants (in ms)
const TOKEN_EXPIRY_MS = 15 * 60 * 1000 // 15 minutes
const WARNING_BEFORE_EXPIRY_MS = 60 * 1000 // Show warning 1 minute before expiry
const WARNING_COUNTDOWN_SECONDS = 60 // 60 second countdown in modal

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showTimeoutModal, setShowTimeoutModal] = useState(false)

  // Refs for timers (prevent stale closures)
  const refreshTimerRef = useRef(null)
  const warningTimerRef = useRef(null)
  const tokenExpiryRef = useRef(null)

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current)
      warningTimerRef.current = null
    }
  }, [])

  // Refresh the access token
  const refreshToken = useCallback(async () => {
    try {
      const response = await fetch(getApiEndpoint('/auth/refresh'), {
        method: 'POST',
        credentials: 'include', // Include httpOnly cookies
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const data = await response.json()

      if (data.success) {
        localStorage.setItem('token', data.token)
        tokenExpiryRef.current = Date.now() + (data.expiresIn || TOKEN_EXPIRY_MS)

        // Update user data if provided
        if (data.user) {
          setUser(data.user)
        }

        return { success: true }
      } else {
        // Refresh failed - clear auth state
        localStorage.removeItem('token')
        setUser(null)
        return { success: false, message: data.message }
      }
    } catch (error) {
      console.error('Token refresh error:', error)
      localStorage.removeItem('token')
      setUser(null)
      return { success: false, message: 'Network error' }
    }
  }, [])

  // Schedule token refresh and warning
  const scheduleTokenRefresh = useCallback((expiresIn = TOKEN_EXPIRY_MS) => {
    clearTimers()

    // Calculate when to show warning (1 minute before expiry)
    const warningTime = expiresIn - WARNING_BEFORE_EXPIRY_MS

    // Schedule warning modal
    if (warningTime > 0) {
      warningTimerRef.current = setTimeout(() => {
        setShowTimeoutModal(true)
      }, warningTime)
    }

    // Store expiry time
    tokenExpiryRef.current = Date.now() + expiresIn
  }, [clearTimers])

  // Handle "Stay Logged In" button
  const handleStayLoggedIn = useCallback(async () => {
    setShowTimeoutModal(false)
    const result = await refreshToken()
    if (result.success) {
      scheduleTokenRefresh()
    }
  }, [refreshToken, scheduleTokenRefresh])

  // Logout function
  const logout = useCallback(async () => {
    clearTimers()
    setShowTimeoutModal(false)

    try {
      // Call server logout to invalidate refresh token
      await fetch(getApiEndpoint('/auth/logout'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })
    } catch (error) {
      console.error('Logout error:', error)
    }

    localStorage.removeItem('token')
    setUser(null)
  }, [clearTimers])

  // Check token on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token')

      if (token) {
        try {
          // Verify token and get user data
          const response = await fetch(getApiEndpoint('/auth/me'), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })

          const data = await response.json()

          if (data.success) {
            setUser(data.user)
            // Token is valid - try to refresh to get fresh expiry
            const refreshResult = await refreshToken()
            if (refreshResult.success) {
              scheduleTokenRefresh()
            }
          } else if (response.status === 401) {
            // Token expired - try to refresh
            const refreshResult = await refreshToken()
            if (refreshResult.success) {
              // Re-fetch user data with new token
              const newToken = localStorage.getItem('token')
              const meResponse = await fetch(getApiEndpoint('/auth/me'), {
                headers: {
                  'Authorization': `Bearer ${newToken}`
                }
              })
              const meData = await meResponse.json()
              if (meData.success) {
                setUser(meData.user)
                scheduleTokenRefresh()
              }
            } else {
              localStorage.removeItem('token')
            }
          } else {
            localStorage.removeItem('token')
          }
        } catch (error) {
          console.error('Auth init error:', error)
          localStorage.removeItem('token')
        }
      }

      setLoading(false)
    }

    initAuth()

    // Cleanup timers on unmount
    return () => clearTimers()
  }, [refreshToken, scheduleTokenRefresh, clearTimers])

  // Login function
  const login = async (email, password) => {
    try {
      const response = await fetch(getApiEndpoint('/auth/login'), {
        method: 'POST',
        credentials: 'include', // Include cookies for refresh token
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (data.success) {
        localStorage.setItem('token', data.token)
        setUser(data.user)
        scheduleTokenRefresh(data.expiresIn || TOKEN_EXPIRY_MS)
        return { success: true, user: data.user }
      } else {
        return { success: false, message: data.message, status: data.status }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, message: 'Network error' }
    }
  }

  // Register function
  const register = async (formData) => {
    try {
      const response = await fetch(getApiEndpoint('/auth/register'), {
        method: 'POST',
        credentials: 'include', // Include cookies for refresh token
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          password: formData.password,
          smsConsent: formData.smsConsent,
        }),
      })

      const data = await response.json()

      if (data.success) {
        localStorage.setItem('token', data.token)
        setUser(data.user)
        scheduleTokenRefresh(data.expiresIn || TOKEN_EXPIRY_MS)
        return { success: true, message: data.message }
      } else {
        return { success: false, message: data.message }
      }
    } catch (error) {
      console.error('Register error:', error)
      return { success: false, message: 'Network error' }
    }
  }

  // Passkey login handler (called after successful passkey auth)
  const handlePasskeyLogin = useCallback((userData, token, expiresIn) => {
    localStorage.setItem('token', token)
    setUser(userData)
    scheduleTokenRefresh(expiresIn || TOKEN_EXPIRY_MS)
  }, [scheduleTokenRefresh])

  // OAuth login handler (called from auth-success page)
  const handleOAuthLogin = useCallback(async (token) => {
    localStorage.setItem('token', token)

    try {
      // Fetch user data
      const response = await fetch(getApiEndpoint('/auth/me'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      const data = await response.json()

      if (data.success) {
        setUser(data.user)
        scheduleTokenRefresh()
        return { success: true, user: data.user }
      } else {
        localStorage.removeItem('token')
        return { success: false, message: data.message }
      }
    } catch (error) {
      console.error('OAuth login error:', error)
      localStorage.removeItem('token')
      return { success: false, message: 'Network error' }
    }
  }, [scheduleTokenRefresh])

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    setUser,
    refreshToken,
    handlePasskeyLogin,
    handleOAuthLogin,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionTimeoutModal
        isOpen={showTimeoutModal}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={logout}
        secondsRemaining={WARNING_COUNTDOWN_SECONDS}
      />
    </AuthContext.Provider>
  )
}
